import { useState, useEffect, useMemo } from 'react'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { AlertCircle, CheckCircle2, Plus, Edit, X } from 'lucide-react'
import ProductTypeQuickCreateModal from './ProductTypeQuickCreateModal'

/**
 * ProductTypeMatchModal
 *
 * Allows users to match imported product types (quality grades like A Class, B Class)
 * to existing product_types in the database, or create new ones.
 *
 * @param {boolean} open - Whether modal is visible
 * @param {function} onClose - Callback when modal is closed
 * @param {function} onConfirm - Callback with type mappings when confirmed
 * @param {function} onCreateType - Callback to create type in database immediately
 * @param {Array} uniqueTypes - Array of unique type strings from import
 * @param {Array} existingTypes - Array of existing product_types from database
 * @param {Object} initialMappings - Pre-existing mappings to restore (for session recovery)
 */
export default function ProductTypeMatchModal({
  open,
  onClose,
  onConfirm,
  onCreateType,
  uniqueTypes = [],
  existingTypes = [],
  initialMappings = {}
}) {
  // State: { "A Class": { action: "match", id: "uuid" } } or { action: "create", name: "A Class", price_modifier_percent: 0, hasPriceModifier: false }
  const [typeMappings, setTypeMappings] = useState({})

  // Local state for newly created types (so they appear in dropdowns)
  const [newlyCreatedTypes, setNewlyCreatedTypes] = useState([])

  // Track which types are being created (for showing create form)
  const [creatingTypes, setCreatingTypes] = useState(new Set())

  // Quick create modal state
  const [showQuickCreateModal, setShowQuickCreateModal] = useState(false)
  const [quickCreateType, setQuickCreateType] = useState(null)

  // Combine existing types with newly created types to make them available for selection
  const availableTypes = useMemo(() => {
    return [...existingTypes, ...newlyCreatedTypes]
  }, [existingTypes, newlyCreatedTypes])

  // Initialize mappings when modal opens
  useEffect(() => {
    if (open && uniqueTypes.length > 0) {
      console.log('🔍 [ProductTypeMatchModal] Initializing mappings:', {
        uniqueTypes,
        initialMappings,
        existingTypes: existingTypes.map(t => ({ id: t.id, name: t.name }))
      })

      const newMappings = {}

      uniqueTypes.forEach(csvType => {
        // First, check if we have a pre-existing mapping (from session recovery)
        if (initialMappings[csvType]) {
          console.log(`✅ [ProductTypeMatchModal] Restoring mapping for "${csvType}":`, initialMappings[csvType])
          newMappings[csvType] = initialMappings[csvType]
        } else {
          console.log(`❌ [ProductTypeMatchModal] No initial mapping for "${csvType}", checking for exact match...`)
          // Try to find exact match (case-insensitive)
          const exactMatch = existingTypes.find(
            dbType => dbType.name.toLowerCase().trim() === csvType.toLowerCase().trim()
          )

          if (exactMatch) {
            console.log(`🎯 [ProductTypeMatchModal] Found exact match for "${csvType}":`, exactMatch.name)
            newMappings[csvType] = {
              action: 'match',
              id: exactMatch.id,
              name: exactMatch.name,
              price_multiplier: exactMatch.price_multiplier
            }
          } else {
            console.log(`⚠️ [ProductTypeMatchModal] No exact match found for "${csvType}"`)
          }
          // Don't auto-create - leave unmapped so user sees dropdown
        }
      })

      console.log('📋 [ProductTypeMatchModal] Final mappings:', newMappings)
      setTypeMappings(newMappings)
      setCreatingTypes(new Set()) // Clear any previous state
      setNewlyCreatedTypes([]) // Clear newly created types from previous sessions
    }
  }, [open, uniqueTypes, existingTypes, initialMappings])

  const handleMatchChange = (csvType, value) => {
    if (value === 'create_new') {
      // Open quick create modal instead of inline form
      setQuickCreateType(csvType)
      setShowQuickCreateModal(true)
    } else if (value === 'disregard') {
      // Mark type to be disregarded - records with this type will be excluded from import
      setTypeMappings(prev => ({
        ...prev,
        [csvType]: {
          action: 'disregard'
        }
      }))

      setCreatingTypes(prev => {
        const newSet = new Set(prev)
        newSet.delete(csvType)
        return newSet
      })
    } else {
      // Matching to existing or newly created type
      const matchedType = availableTypes.find(t => t.id === value)

      // Both existing and newly created types use the same 'match' action now
      setTypeMappings(prev => ({
        ...prev,
        [csvType]: {
          action: 'match',
          id: matchedType.id,
          name: matchedType.name,
          price_multiplier: matchedType.price_multiplier
        }
      }))

      setCreatingTypes(prev => {
        const newSet = new Set(prev)
        newSet.delete(csvType)
        return newSet
      })
    }
  }

  const handleQuickCreateConfirm = async (typeData) => {
    if (!quickCreateType) return

    try {
      // Create the type in the database immediately
      const newType = await onCreateType({
        name: typeData.name,
        price_multiplier: typeData.price_multiplier ?? 1.0,
        active: true
      })

      // Add to newly created types list with isNew flag
      const typeWithFlag = { ...newType, isNew: true }
      setNewlyCreatedTypes(prev => [...prev, typeWithFlag])

      // Save the mapping with the actual database ID
      setTypeMappings(prev => ({
        ...prev,
        [quickCreateType]: {
          action: 'match', // Changed from 'create' to 'match' since it now exists in DB
          id: newType.id,
          name: newType.name,
          price_multiplier: newType.price_multiplier
        }
      }))

      // Mark as being created (for UI badge)
      setCreatingTypes(prev => new Set(prev).add(quickCreateType))

      // Close modal
      setShowQuickCreateModal(false)
      setQuickCreateType(null)
    } catch (error) {
      console.error('Failed to create product type:', error)
      alert('Failed to create product type: ' + error.message)
    }
  }

  const handleConfirm = () => {
    // Validate: all types must be mapped
    const allMapped = uniqueTypes.every(type => typeMappings[type])
    if (!allMapped) {
      alert('Please map all product types before proceeding')
      return
    }

    // Validate: types being created must have names
    const allCreatingHaveNames = uniqueTypes
      .filter(type => typeMappings[type].action === 'create')
      .every(type => typeMappings[type].name?.trim())

    if (!allCreatingHaveNames) {
      alert('Please provide names for all new product types')
      return
    }

    onConfirm(typeMappings)
  }

  const allTypesMatched = uniqueTypes.length > 0 && uniqueTypes.every(type => typeMappings[type])

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="odin-modal-large max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Match Product Types</DialogTitle>
          <DialogDescription>
            Map imported product types (quality grades) to existing types in your database, or create new ones.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 py-4">
          {uniqueTypes.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No product types found in import data
            </div>
          ) : (
            uniqueTypes.map(csvType => {
              const mapping = typeMappings[csvType]
              const isCreating = creatingTypes.has(csvType)
              const isMatched = mapping?.action === 'match'
              const isDisregarded = mapping?.action === 'disregard'

              return (
                <div key={csvType} className="border rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{csvType}</span>
                      {isMatched && (
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          Matched
                        </Badge>
                      )}
                      {isCreating && (
                        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                          <Plus className="w-3 h-3 mr-1" />
                          New
                        </Badge>
                      )}
                      {isDisregarded && (
                        <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                          <X className="w-3 h-3 mr-1" />
                          Disregarded
                        </Badge>
                      )}
                    </div>
                  </div>

                  <div className="space-y-3">
                    {isDisregarded ? (
                      // Show disregarded type with change option
                      <div className="space-y-2">
                        <div className="flex items-center justify-between pl-4 border-l-2 border-red-200 bg-red-50/50 p-3 rounded">
                          <div className="flex-1">
                            <p className="text-sm font-medium text-red-700">
                              Records with this type will be excluded from import
                            </p>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              // Clear the mapping to show dropdown again
                              setTypeMappings(prev => {
                                const newMappings = { ...prev }
                                delete newMappings[csvType]
                                return newMappings
                              })
                              setCreatingTypes(prev => {
                                const newSet = new Set(prev)
                                newSet.delete(csvType)
                                return newSet
                              })
                            }}
                          >
                            Change
                          </Button>
                        </div>
                      </div>
                    ) : isCreating && mapping ? (
                      // Show configured type with edit/change options
                      <div className="space-y-2">
                        <div className="flex items-center justify-between pl-4 border-l-2 border-blue-200 bg-blue-50/50 p-3 rounded">
                          <div className="flex-1">
                            <p className="text-sm font-medium">
                              Will create: <span className="text-blue-700">{mapping.name}</span>
                            </p>
                            {mapping.price_multiplier != null && mapping.price_multiplier !== 1.0 && (
                              <p className="text-xs text-gray-600">
                                Price multiplier: {mapping.price_multiplier}×
                              </p>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setQuickCreateType(csvType)
                                setShowQuickCreateModal(true)
                              }}
                              className="flex items-center gap-1"
                            >
                              <Edit className="w-3 h-3" />
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                // Clear the mapping to show dropdown again
                                setTypeMappings(prev => {
                                  const newMappings = { ...prev }
                                  delete newMappings[csvType]
                                  return newMappings
                                })
                                setCreatingTypes(prev => {
                                  const newSet = new Set(prev)
                                  newSet.delete(csvType)
                                  return newSet
                                })
                              }}
                            >
                              Change
                            </Button>
                          </div>
                        </div>
                      </div>
                    ) : isMatched && mapping ? (
                      // Show matched type with change option
                      <div className="space-y-2">
                        <div className="flex items-center justify-between pl-4 border-l-2 border-green-200 bg-green-50/50 p-3 rounded">
                          <div className="flex-1">
                            <p className="text-sm font-medium">
                              Matched to: <span className="text-green-700">{mapping.name}</span>
                            </p>
                            {mapping.price_multiplier != null && mapping.price_multiplier !== 1.0 && (
                              <p className="text-xs text-gray-600">
                                Price multiplier: {mapping.price_multiplier}×
                              </p>
                            )}
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              // Clear the mapping to show dropdown again
                              setTypeMappings(prev => {
                                const newMappings = { ...prev }
                                delete newMappings[csvType]
                                return newMappings
                              })
                              setCreatingTypes(prev => {
                                const newSet = new Set(prev)
                                newSet.delete(csvType)
                                return newSet
                              })
                            }}
                          >
                            Change
                          </Button>
                        </div>
                      </div>
                    ) : (
                      // Show dropdown to select action
                      <div>
                        <Label htmlFor={`type-select-${csvType}`}>Action</Label>
                        <Select
                          value={mapping?.action === 'match' || mapping?.action === 'match-new' ? mapping.id : undefined}
                          onValueChange={(value) => handleMatchChange(csvType, value)}
                        >
                          <SelectTrigger id={`type-select-${csvType}`}>
                            <SelectValue placeholder="Select action" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="create_new">Create New Type</SelectItem>
                            <SelectItem value="disregard">Disregard (Exclude from import)</SelectItem>
                            {availableTypes.length > 0 && (
                              <>
                                <div className="px-2 py-1.5 text-xs font-semibold text-gray-500">
                                  Match to Existing
                                </div>
                                {availableTypes.map(type => {
                                  const multiplier = parseFloat(type.price_multiplier) ?? 1.0
                                  let suffix = ''
                                  if (multiplier === 0) suffix = ' (Free)'
                                  else if (multiplier !== 1.0) suffix = ` (${multiplier}×)`

                                  return (
                                    <SelectItem key={type.id} value={type.id}>
                                      {type.name}{suffix}
                                    </SelectItem>
                                  )
                                })}
                              </>
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                </div>
              )
            })
          )}
        </div>

        <DialogFooter className="border-t pt-4">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2 text-sm">
              {allTypesMatched ? (
                <>
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                  <span className="text-green-600">All types mapped</span>
                </>
              ) : (
                <>
                  <AlertCircle className="w-4 h-4 text-amber-600" />
                  <span className="text-amber-600">
                    {uniqueTypes.filter(t => !typeMappings[t]).length} type(s) remaining
                  </span>
                </>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button onClick={handleConfirm} disabled={!allTypesMatched}>
                Continue to Products
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>

      {/* Quick Create Modal */}
      <ProductTypeQuickCreateModal
        open={showQuickCreateModal}
        onClose={() => {
          setShowQuickCreateModal(false)
          // Don't clear quickCreateType immediately - let React finish rendering first
          setTimeout(() => setQuickCreateType(null), 0)
        }}
        onConfirm={handleQuickCreateConfirm}
        defaultName={quickCreateType || ''}
        initialValues={quickCreateType ? typeMappings[quickCreateType] : null}
      />
    </Dialog>
  )
}
